import { useState } from 'react'
import { Dices, HandCoins } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { seatStyle } from '../helpers'
import type { BoardProps } from './types'

/* ------------------------------------------------------------------ pig */

interface PigView {
  scores: number[]
  turn: number
  pot: number
  roll: { value: number; by: number; n: number } | null
  bust: boolean
  target: number
}
const FACE = ['', '⚀', '⚁', '⚂', '⚃', '⚄', '⚅']

export function Pig({ view, seat, over, busy, send, players, people }: BoardProps<PigView>) {
  const name = (s: number) => (s === seat ? 'You' : (people.get(players[s] ?? '')?.displayName.split(' ')[0] ?? 'Player'))
  const play = seat !== null && seat === view.turn && !over && !busy
  return (
    <div className="mx-auto grid w-full max-w-md gap-4 text-center">
      <ul className="grid gap-1.5" aria-label="Scores">
        {view.scores.map((sc, s) => (
          <li key={s} className={cn('flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm', view.turn === s && !over ? cn(seatStyle(s).border, seatStyle(s).soft) : 'border-border bg-card')}>
            <span className={cn('size-2.5 rounded-full', seatStyle(s).bg)} />
            <span className="flex-1 text-left font-medium">{name(s)}</span>
            <span className="h-2 w-24 overflow-hidden rounded-full bg-muted">
              <span className={cn('block h-full', seatStyle(s).bg)} style={{ width: `${Math.min(100, (sc / view.target) * 100)}%` }} />
            </span>
            <span className="num w-10 text-right font-semibold tabular-nums">{sc}</span>
          </li>
        ))}
      </ul>
      <div className="grid place-items-center gap-1 rounded-xl border border-border bg-card py-4">
        <span key={view.roll?.n ?? 0} className={cn('text-7xl leading-none', view.bust && 'text-destructive')} aria-label={view.roll ? `Rolled a ${view.roll.value}` : 'No roll yet'}>
          {view.roll ? FACE[view.roll.value] : '🎲'}
        </span>
        <p className="text-sm text-text-secondary" aria-live="polite">
          {view.bust && view.roll ? `${name(view.roll.by)} rolled a 1 and lost the pot.` : over ? '' : `${name(view.turn)}${view.turn === seat ? ' have' : ' has'} ${view.pot} in the pot.`}
        </p>
      </div>
      <div className="flex items-center justify-center gap-3">
        <Button type="button" disabled={!play} onClick={() => send({ do: 'roll' })}>
          <Dices className="size-4" /> Roll
        </Button>
        <Button type="button" variant="outline" disabled={!play || view.pot === 0} onClick={() => send({ do: 'hold' })}>
          <HandCoins className="size-4" /> Hold {view.pot ? `(+${view.pot})` : ''}
        </Button>
      </div>
      <p className="text-xs text-text-muted">Roll to add to the pot. A 1 loses it and ends your turn. Hold to bank it. First to {view.target} wins.</p>
    </div>
  )
}

/* ------------------------------------------------------------------ hangman */

interface HangmanView {
  mask: (string | null)[]
  guessed: { letter: string; by: number; right: boolean }[]
  lives: number
  maxLives: number
  scores: number[]
  turn: number
}
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

export function Hangman({ view, seat, over, busy, send, players, people }: BoardProps<HangmanView>) {
  const [word, setWord] = useState('')
  const play = seat !== null && seat === view.turn && !over && !busy
  const name = (s: number) => (s === seat ? 'You' : (people.get(players[s] ?? '')?.displayName.split(' ')[0] ?? 'Player'))
  const by = new Map(view.guessed.map((g) => [g.letter, g]))
  return (
    <div className="mx-auto grid w-full max-w-lg gap-4 text-center">
      <div className="flex items-center justify-center gap-1" aria-label={`${view.lives} of ${view.maxLives} lives left`}>
        {Array.from({ length: view.maxLives }, (_, i) => (
          <span key={i} className={cn('text-xl transition-opacity', i < view.lives ? '' : 'opacity-20 grayscale')} aria-hidden>
            ❤️
          </span>
        ))}
      </div>
      <p className="flex flex-wrap items-center justify-center gap-1.5" aria-label="The word">
        {view.mask.map((ch, i) => (
          <span key={i} className={cn('grid h-12 w-9 place-items-center rounded-md border-b-4 bg-muted text-2xl font-bold', ch ? 'border-brand text-foreground' : 'border-border-strong text-transparent')}>
            {ch ?? '·'}
          </span>
        ))}
      </p>
      <div className="grid grid-cols-9 gap-1.5 sm:grid-cols-[repeat(13,minmax(0,1fr))]" role="group" aria-label="Letters">
        {LETTERS.map((l) => {
          const g = by.get(l)
          return (
            <button
              key={l}
              type="button"
              disabled={!play || Boolean(g)}
              onClick={() => send({ letter: l })}
              className={cn('grid aspect-square place-items-center rounded-md border text-sm font-semibold', g ? (g.right ? cn(seatStyle(g.by).soft, 'border-transparent') : 'border-transparent bg-muted text-text-muted line-through') : 'border-border bg-card', play && !g && 'hover:bg-accent')}
              title={g ? `${name(g.by)} guessed ${l}` : undefined}
            >
              {l}
            </button>
          )
        })}
      </div>
      {play ? (
        <form
          className="mx-auto flex w-full max-w-xs items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (word.trim()) {
              send({ word: word.trim() })
              setWord('')
            }
          }}
        >
          <Input value={word} onChange={(e) => setWord(e.target.value.replace(/[^a-zA-Z]/g, ''))} placeholder="Know it? Type the word" aria-label="Guess the whole word" maxLength={20} />
          <Button type="submit" variant="outline" disabled={word.trim().length < 2}>
            Guess
          </Button>
        </form>
      ) : null}
      <ul className="flex flex-wrap items-center justify-center gap-2 text-sm" aria-label="Scores">
        {view.scores.map((sc, s) => (
          <li key={s} className={cn('flex items-center gap-1.5 rounded-full border px-3 py-1', view.turn === s && !over ? cn(seatStyle(s).border, seatStyle(s).soft) : 'border-border bg-card')}>
            <span className={cn('size-2.5 rounded-full', seatStyle(s).bg)} /> {name(s)} <span className="num font-semibold tabular-nums">{sc}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-text-muted">10 points for each letter you find, 30 for finishing the word, 50 for naming it outright. A wrong guess costs everyone a life.</p>
    </div>
  )
}
